import { FC, useEffect, useMemo, useState } from "react";
import {
  Col,
  FormGroup,
  FormLabel,
  FormSelect,
  Modal,
  Row,
} from "react-bootstrap";
import "../custom.css";
import ButtonWrapper from "./ButtonWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ReactComponent as Electric } from "../images/electric.svg";
import { ReactComponent as Pram } from "../images/pram.svg";
import { ReactComponent as Accessible } from "../images/wheelchair.svg";
import { ReactComponent as Car } from "../images/car.svg";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import {
  useCreateReservation,
  useCancelReservation,
  useCalculateReservation,
} from "../api/hooks";
import {
  ProfileResponse,
  SiteResponse,
  ReservationRequest,
  VehicleResponse,
  CancelReservationResponse,
} from "../api/ApiClient";
import { toast } from "react-toastify";

// Booking mode type for strata users
type BookingMode = "own" | "visitor";

// Extended Reservation interface for the component
interface Reservation extends ReservationRequest {
  id?: string;
  siteName?: string;
  status?: string;
  price?: number;
  error?: string;
  vehiclePlate?: string;
  visitorName?: string;
  visitorPhone?: string;
  type?: string;
}

function onlyUnique<T>(value: T, index: number, self: T[]): boolean {
  return self.indexOf(value) === index;
}

const inOneHour = new Date(Date.now() + 60 * 60 * 1000);

/**
 * Converts a date to a string in the format "YYYY-MM-DD"
 */
const toLocStrDate = (date: Date): string => {
  return date.toLocaleDateString("en-AU").split("/").reverse().join("-");
};

/**
 * Converts a date to a string in the format "HH:MM"
 */
const toLocStrTime = (date: Date): string => {
  return date
    .toLocaleTimeString(undefined, { hour12: false })
    .split(":")
    .slice(0, 2)
    .join(":");
};

// loop and create times for every 15 minutes
const times = [] as string[];
for (let i = 0; i < 24; i++) {
  for (let j = 0; j < 60; j += 15) {
    const hour = i < 10 ? "0" + i : i;
    const minute = j < 10 ? "0" + j : j;

    times.push(`${hour}:${minute}`);
  }
}

const defaultValues: Reservation = {
  city: "",
  vehicleId: "",
  siteId: "",
  siteName: "",
  // set default date and time to australia date and time one hour from now
  startDate: toLocStrDate(new Date(Date.now())),
  startTime: toLocStrTime(new Date(Date.now())),

  // set end time to 1 hour from now
  endDate: toLocStrDate(inOneHour),
  endTime: toLocStrTime(inOneHour),

  status: "Pending",
  price: 0,
  bayType: "",
};

const ReservationDetails: FC<{
  reservation: Reservation;
  profile: ProfileResponse;
  vehicles: VehicleResponse[];
  sites: SiteResponse[];
}> = ({ reservation, profile, sites, vehicles }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createReservationMutation = useCreateReservation();
  const cancelReservationMutation = useCancelReservation();
  const calculateReservationMutation = useCalculateReservation();
  const queryClient = useQueryClient();

  const readOnly = useMemo(() => !!reservation?.id, [reservation?.id]);

  // Check if cancellation is allowed (more than 1 hour before start time in site timezone)
  const cancellable = useMemo(() => {
    if (!reservation?.id) return false; // Can't cancel non-existent reservation
    if (!reservation?.startDate || !reservation?.startTime) return true; // Allow if no time info
    
    const siteForReservation = sites.find(s => s.id === reservation?.siteId);
    const timezone = siteForReservation?.timeZone ?? 'Australia/Sydney';
    const startDateTimeStr = `${reservation.startDate}T${reservation.startTime}`;
    const startDateTimeUtc = fromZonedTime(startDateTimeStr, timezone);
    const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
    
    return startDateTimeUtc >= oneHourFromNow;
  }, [reservation?.id, reservation?.startDate, reservation?.startTime, reservation?.siteId, sites]);

  // Check for siteId in query params for pre-selection
  const preselectedSiteId = searchParams.get("siteId");

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { isDirty, dirtyFields },
  } = useForm<Reservation>({ defaultValues: reservation, values: reservation });

  // Pre-select site from query param - set city first, then siteId after a tick
  useEffect(() => {
    if (preselectedSiteId && !reservation?.id && sites.length > 0) {
      const site = sites.find(s => s.id === preselectedSiteId);
      if (site) {
        // Set city first
        if (site.city) {
          setValue("city", site.city);
        }
        // Set siteId after a small delay to ensure city filter is applied
        setTimeout(() => {
          setValue("siteId", preselectedSiteId);
        }, 0);
      }
    }
  }, [preselectedSiteId, reservation?.id, sites, setValue]);

  const startDate = watch("startDate") || defaultValues.startDate;
  const startTime = watch("startTime") || defaultValues.startTime;
  const endDate = watch("endDate") || defaultValues.endDate;
  const endTime = watch("endTime") || defaultValues.endTime;
  const vehicleId = watch("vehicleId") || defaultValues.vehicleId;

  const city = watch("city") || defaultValues.city;
  const siteId = watch("siteId") || defaultValues.siteId;
  const bayType = watch("bayType") || defaultValues.bayType;

  // Booking mode state for strata users
  const [bookingMode, setBookingMode] = useState<BookingMode>("own");
  const [visitorPlate, setVisitorPlate] = useState("");
  const [visitorName, setVisitorName] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");

  const startDateIsToday = useMemo(
    () => startDate === toLocStrDate(new Date(Date.now())),
    [startDate]
  );

  const start = useMemo(
    () => new Date(`${startDate} ${startTime}`),
    [startDate, startTime]
  );
  const end = useMemo(
    () => new Date(`${endDate} ${endTime}`),
    [endDate, endTime]
  );

  const site = useMemo(
    () => sites.find((s) => s.id === siteId),
    [sites, siteId]
  );

  // Check if user has strata access to the selected site
  const hasStrataAccess = useMemo(
    () => site?.accessTypes?.includes("strata") ?? false,
    [site]
  );

  // Reset booking mode when site changes (if new site doesn't have strata)
  useEffect(() => {
    if (!hasStrataAccess) {
      setBookingMode("own");
      setVisitorPlate("");
      setVisitorName("");
      setVisitorPhone("");
    }
  }, [hasStrataAccess]);

  // Reset EV bay type when switching to visitor mode
  useEffect(() => {
    if (bookingMode === "visitor" && bayType === "EV") {
      setValue("bayType", "");
    }
  }, [bookingMode, bayType, setValue]);

  const vehicle = useMemo(
    () => vehicles?.find((v) => v.id === vehicleId),
    [vehicles, vehicleId]
  );

  const currentLocalTimeAtSite = useMemo(
    () => toZonedTime(new Date(), site?.timeZone ?? "Australia/Sydney"),
    [site]
  );

  const isValidForFetching = useMemo(() => {
    const values = getValues();

    // check if values is empty object
    if (Object.keys(values).length === 0) {
      return false;
    }

    // check if all values are not empty (vehicle info doesn't affect price)
    const result =
      values &&
      city !== "" &&
      siteId !== "" &&
      values.startDate !== "" &&
      values.startTime !== "" &&
      values.endDate !== "" &&
      values.endTime !== "" &&
      !readOnly &&
      start < end;

    return result;
  }, [getValues, readOnly, start, end, city, siteId]);

  useEffect(() => {
    if (readOnly) return;

    // if start + 1 hour is after end, set end to start + 1 hour (and the end time hasn't been changed already)
    if (
      start.getTime() + 60 * 60 * 1000 > end.getTime() &&
      !(isDirty && dirtyFields.endTime)
    ) {
      const newEnd = new Date(start.getTime() + 60 * 60 * 1000);
      setValue("endDate", newEnd.toISOString().split("T")[0]);
      setValue("endTime", newEnd.toTimeString().split(" ")[0].substring(0, 5));
    }
  }, [readOnly, start, end, isDirty, dirtyFields.endTime, setValue]);

  // when city changes, reset siteId
  useEffect(() => {
    if (readOnly) return;
    setValue("siteId", "");
    setValue("bayType", "");
  }, [setValue, city, readOnly]);

  const { data, isFetching } = useQuery({
    queryKey: ["price", { city, siteId, start, end, bayType, bookingMode }],
    queryFn: async () => {
      try {
        const result = await calculateReservationMutation.mutateAsync({
          city,
          siteId,
          bayType,
          endDate,
          endTime,
          startDate,
          startTime,
          bookingFor: bookingMode === "visitor" ? "visitor" : "self",
        });
        return result;
      } catch (error) {
        console.error("Error calculating reservation:", error);
        return {
          data: {
            error: "Failed to calculate price",
            status: "Error",
            statusMessage: "",
            price: 0,
          },
        };
      }
    },
    refetchOnWindowFocus: false,
    enabled: isValidForFetching,
  });

  useEffect(() => {
    if (data?.data) {
      setValue("status", data.data.status || "");
      setValue("price", data.data.price || 0);
      setValue("error", data.data.error || "");

      // Show error toast if there's a non-empty error message
      if (data.data.error && data.data.error.trim() !== "") {
        toast.error(data.data.error);
      }
    }
  }, [data, setValue]);

  const onSubmit: SubmitHandler<Reservation> = async (data) => {
    try {
      // For visitor bookings, use visitor plate instead of vehicleId
      const requestData = bookingMode === "visitor"
        ? { ...data, vehicleId: undefined, vehiclePlate: visitorPlate, visitorName, visitorPhone, bookingFor: "visitor" }
        : { ...data, bookingFor: "self" };

      const result = await createReservationMutation.mutateAsync(requestData);

      // Check if the response contains an error message
      if (result?.data?.error && result.data.error.trim() !== "") {
        toast.error(result.data.error);
        return; // Don't proceed with success actions
      }

      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      navigate("/reservations");
    } catch (error: any) {
      console.error("Error creating reservation:", error);

      // Show error toast if there's an error message
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to create reservation";
      if (errorMessage && errorMessage.trim() !== "") {
        toast.error(errorMessage);
      }
    }
  };

  const cancel = async () => {
    if (!reservation.id) return;
    try {
      const result = await cancelReservationMutation.mutateAsync(
        reservation.id
      );

      // Check if the response contains an error message
      const cancelResponse = result.data as CancelReservationResponse;
      if (cancelResponse?.error && cancelResponse.error.trim() !== "") {
        toast.error(cancelResponse.error);
        return; // Don't proceed with success actions
      }

      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      navigate("/reservations");
    } catch (error: any) {
      console.error("Error cancelling reservation:", error);

      // Show error toast if there's an error message
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to cancel reservation";
      if (errorMessage && errorMessage.trim() !== "") {
        toast.error(errorMessage);
      }
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (!value) return "";

    return value.toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
    });
  };

  const watchCity = watch("city") || reservation?.city;
  const price = watch("price") || reservation?.price;

  const canSelectTime = (time: string, type: string) => {
    const parseTime = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(":").map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    };

    if (type === "start") {
      if (startDateIsToday) {
        return parseTime(time) > currentLocalTimeAtSite;
      } else {
        return true;
      }
    }

    if (type === "end") {
      // check if end date is the same as start date
      if (startDate === endDate) {
        const startTime = parseTime(
          `${start.getHours()}:${start.getMinutes()}`
        );
        const endTime = parseTime(time);

        return endTime > startTime;
      } else {
        return true;
      }
    }
  };

  useEffect(() => {
    // if startDate > endDate, set endDate to startDate
    if (startDate && endDate && startDate > endDate) {
      setValue("endDate", startDate);
    }
  }, [startDate, endDate, setValue]);

  const toggleBayType = (type: string) => {
    if (readOnly) return;
    
    if (type !== "") {
      if (!site || !site.bayTypes?.includes(type)) {
        return;
      }
      // EV bays only available for own vehicle bookings with an electric vehicle
      if (type === "EV" && (bookingMode === "visitor" || !vehicle?.isElectric)) {
        return;
      }
    }

    if (bayType === type) {
      setValue("bayType", "");
    } else {
      setValue("bayType", type);
    }
  };

  const formatTime = (time: string) => {
    // take in string like 12:00 and return 12:00 PM
    const hour = parseInt(time.split(":")[0]);
    const minute = parseInt(time.split(":")[1]);
    let minuteString = time.split(":")[1];

    // ensure leading zero on minute
    if (minute < 10) {
      minuteString = `0${minute}`;
    }

    if (hour < 12) {
      return `${hour}:${minuteString} AM`;
    }

    if (hour === 12) {
      return `${hour}:${minuteString} PM`;
    }

    return `${hour - 12}:${minuteString} PM`;
  };


  const disableSubmit = readOnly ||
                    !!getValues("error") ||
                  getValues("status") !== "Pending" ||
                  createReservationMutation.isPending;

  



  return (
    <Modal
      show={true}
      dialogClassName="modalReservationEditForm"
      centered
      backdrop="static"
    >
      <Modal.Header style={{ backgroundColor: "white" }}>
        <Col>
          <h2>Reservation</h2>
        </Col>
        <Col xs={3} className="text-end">
          <ButtonWrapper
            buttonText="Close x"
            fullWidth={true}
            onClick={() => navigate("/reservations")}
          />
        </Col>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="formStyle">
          <FormGroup>
            <FormLabel>City</FormLabel>
            <FormSelect
              {...register("city")}
              defaultValue={reservation?.city || ""}
              disabled={readOnly}
            >
              <option value={""}>Choose a City</option>
              {sites
                .map((site) => site.city)
                .filter(onlyUnique)
                .map((city, i) => {
                  return <option key={`city-${i}`}>{city}</option>;
                })}
            </FormSelect>
          </FormGroup>

          <FormGroup className="mt-2">
            <FormLabel>Site</FormLabel>
            <FormSelect {...register("siteId")} disabled={readOnly}>
              <option value={""}>Choose a Site</option>
              {sites
                .filter((s) => s.city === watchCity)
                .map((site, i) => {
                  return (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  );
                })}
            </FormSelect>
          </FormGroup>

          {/* Booking mode selector - only shown for strata sites */}
          {hasStrataAccess && !readOnly && (
            <FormGroup className="mt-2">
              <FormLabel>Booking For</FormLabel>
              <div className="d-flex gap-2">
                <div
                  className={
                    "w-100 d-flex justify-content-center flex-grow-1" +
                    (bookingMode === "own" ? " orangeButton" : " orangeButton-outline")
                  }
                  onClick={() => setBookingMode("own")}
                >
                  My Vehicle
                </div>
                <div
                  className={
                    "w-100 d-flex justify-content-center flex-grow-1" +
                    (bookingMode === "visitor" ? " orangeButton" : " orangeButton-outline")
                  }
                  onClick={() => setBookingMode("visitor")}
                >
                  Visitor
                </div>
              </div>
            </FormGroup>
          )}

          {/* Vehicle selection - shown for own vehicle bookings */}
          {bookingMode === "own" && !readOnly && (
            <FormGroup className="mt-2">
              <FormLabel>Vehicle</FormLabel>
              <FormSelect {...register("vehicleId")} disabled={readOnly}>
                {vehicles?.map((vehicle, i) => {
                  return (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.plate}
                    </option>
                  );
                })}
              </FormSelect>
            </FormGroup>
          )}

          {/* Read-only vehicle display for non-Strata reservations */}
          {readOnly && reservation?.type?.toUpperCase() !== "STRATA" && reservation?.vehicleId && (
            <FormGroup className="mt-2">
              <FormLabel>Vehicle</FormLabel>
              <FormSelect {...register("vehicleId")} disabled={true}>
                {vehicles?.map((vehicle, i) => {
                  return (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.plate}
                    </option>
                  );
                })}
              </FormSelect>
            </FormGroup>
          )}

          {/* Read-only plate display for Strata (visitor) reservations */}
          {readOnly && reservation?.type?.toUpperCase() === "STRATA" && reservation?.vehiclePlate && (
            <>
              <FormGroup className="mt-2">
                <FormLabel>Visitor Plate</FormLabel>
                <input
                  className="form-control"
                  type="text"
                  value={reservation.vehiclePlate}
                  disabled={true}
                />
              </FormGroup>
              {reservation?.visitorName && (
                <FormGroup className="mt-2">
                  <FormLabel>Visitor Name</FormLabel>
                  <input
                    className="form-control"
                    type="text"
                    value={reservation.visitorName}
                    disabled={true}
                  />
                </FormGroup>
              )}
              {reservation?.visitorPhone && (
                <FormGroup className="mt-2">
                  <FormLabel>Visitor Phone</FormLabel>
                  <input
                    className="form-control"
                    type="text"
                    value={reservation.visitorPhone}
                    disabled={true}
                  />
                </FormGroup>
              )}
            </>
          )}

          {/* Visitor plate input - shown for visitor bookings */}
          {bookingMode === "visitor" && !readOnly && (
            <>
              <FormGroup className="mt-2">
                <FormLabel>Visitor Plate <span className="text-danger">*</span></FormLabel>
                <input
                  className={`form-control ${!visitorPlate.trim() ? "is-invalid" : ""}`}
                  type="text"
                  placeholder="Enter visitor's plate number"
                  value={visitorPlate}
                  onChange={(e) => setVisitorPlate(e.target.value.toUpperCase())}
                />
                {!visitorPlate.trim() && (
                  <div className="invalid-feedback">Visitor plate is required</div>
                )}
              </FormGroup>
              <FormGroup className="mt-2">
                <FormLabel>Visitor Name <span className="text-danger">*</span></FormLabel>
                <input
                  className={`form-control ${!visitorName.trim() ? "is-invalid" : ""}`}
                  type="text"
                  placeholder="Enter visitor's name"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                />
                {!visitorName.trim() && (
                  <div className="invalid-feedback">Visitor name is required</div>
                )}
              </FormGroup>
              <FormGroup className="mt-2">
                <FormLabel>Visitor Phone <span className="text-danger">*</span></FormLabel>
                <input
                  className={`form-control ${!visitorPhone.trim() ? "is-invalid" : ""}`}
                  type="tel"
                  placeholder="Enter visitor's phone number"
                  value={visitorPhone}
                  onChange={(e) => setVisitorPhone(e.target.value)}
                />
                {!visitorPhone.trim() && (
                  <div className="invalid-feedback">Visitor phone is required</div>
                )}
              </FormGroup>
            </>
          )}

          <Row>
            <Col>
              <FormGroup className="mt-2">
                <FormLabel>Bay Features</FormLabel>
                <div className="d-flex gap-2">
                  <div
                    className={
                      "w-100 d-flex justify-content-center flex-grow-1" +
                      (bayType === ""
                        ? " orangeButton"
                        : " orangeButton-outline") +
                      (readOnly ? " disabledOption" : "")
                    }
                    onClick={() => toggleBayType("")}
                    style={readOnly ? { cursor: "default" } : undefined}
                  >
                    <Car style={{ width: 24, marginRight: "0.5rem" }} />{" "}
                    Standard
                  </div>
                  <div
                    className={
                      "w-100 d-flex justify-content-center flex-grow-1" +
                      (bayType === "EV"
                        ? " orangeButton"
                        : " orangeButton-outline") +
                      (readOnly ||
                      !(site &&
                      site.bayTypes?.includes("EV") &&
                      bookingMode === "own" &&
                      vehicle?.isElectric)
                        ? " disabledOption"
                        : "")
                    }
                    onClick={() => toggleBayType("EV")}
                    style={readOnly ? { cursor: "default" } : undefined}
                  >
                    <Electric style={{ width: 24, marginRight: "0.5rem" }} /> EV
                  </div>
                  <div
                    className={
                      "w-100 d-flex justify-content-center flex-grow-1" +
                      (bayType === "PWP"
                        ? " orangeButton"
                        : " orangeButton-outline") +
                      (readOnly || !(site && site.bayTypes?.includes("PWP"))
                        ? " disabledOption"
                        : "")
                    }
                    onClick={() => toggleBayType("PWP")}
                    style={readOnly ? { cursor: "default" } : undefined}
                  >
                    <Pram style={{ width: 24, marginRight: "0.5rem" }} /> Pram
                  </div>
                  <div
                    className={
                      "w-100 d-flex justify-content-center flex-grow-1" +
                      (bayType === "Accessible"
                        ? " orangeButton"
                        : " orangeButton-outline") +
                      (readOnly || !(site && site.bayTypes?.includes("Accessible"))
                        ? " disabledOption"
                        : "")
                    }
                    onClick={() => toggleBayType("Accessible")}
                    style={readOnly ? { cursor: "default" } : undefined}
                  >
                    <Accessible style={{ width: 24, marginRight: "0.5rem" }} />{" "}
                    Accessible
                  </div>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col>
              <FormGroup>
                <FormLabel>Start Date</FormLabel>
                <input
                  className="form-control"
                  type="date"
                  defaultValue={defaultValues.startDate}
                  disabled={readOnly}
                  min={toLocStrDate(new Date())}
                  {...register("startDate")}
                />
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <FormLabel>Start Time</FormLabel>
                {/* <input
                  className="form-control"
                  type="time"
                  defaultValue={defaultValues.startTime}
                  disabled={readOnly}
                  {...register("startTime")}
                /> */}

                <select
                  className="form-control"
                  {...register("startTime")}
                  disabled={readOnly}
                >
                  {times.map((time, i) => {
                    return (
                      <option
                        key={`time-${i}`}
                        value={time}
                        disabled={!canSelectTime(time, "start")}
                      >
                        {formatTime(time)}
                      </option>
                    );
                  })}
                </select>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col>
              <FormGroup>
                <FormLabel>End Date</FormLabel>
                <input
                  className="form-control"
                  type="date"
                  defaultValue={defaultValues.endDate}
                  disabled={readOnly}
                  min={toLocStrDate(start)}
                  {...register("endDate")}
                />
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <FormLabel>End Time</FormLabel>
                {/* <input
                  className="form-control"
                  type="time"
                  defaultValue={defaultValues.endTime}
                  disabled={readOnly}
                  {...register("endTime")}
                /> */}

                <select
                  className="form-control"
                  {...register("endTime")}
                  disabled={readOnly}
                >
                  {times.map((time, i) => {
                    return (
                      <option
                        key={`time-${i}`}
                        value={time}
                        disabled={!canSelectTime(time, "end")}
                      >
                        {formatTime(time)}
                      </option>
                    );
                  })}
                </select>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <FormLabel>Status</FormLabel>
            {readOnly ? (
              <div>
                <span 
                  className="badge"
                  style={{
                    backgroundColor: reservation.status === "Confirmed" ? 'rgba(25, 135, 84, 0.1)' : 'rgba(108, 117, 125, 0.1)',
                    color: reservation.status === "Confirmed" ? '#198754' : '#6c757d',
                    border: reservation.status === "Confirmed" ? '1px solid rgba(25, 135, 84, 0.2)' : '1px solid rgba(108, 117, 125, 0.2)',
                    fontWeight: 500,
                    padding: '0.35em 0.65em',
                    fontSize: '0.875em'
                  }}
                >
                  {reservation.status || "Unknown"}
                </span>
              </div>
            ) : isFetching || calculateReservationMutation.isPending ? (
              <label className="status">Loading...</label>
            ) : (
              <label className="status">
                {siteId === "" || data?.data?.error ? (
                  <span className="error">{data?.data?.error}&nbsp;</span>
                ) : (
                  data?.data?.statusMessage || getValues("status")
                )}
              </label>
            )}
          </FormGroup>

          <FormGroup>
            <FormLabel>Price</FormLabel>
            {readOnly ? (
              <label className="status">{formatCurrency(reservation.price ?? 0)}</label>
            ) : isFetching || calculateReservationMutation.isPending ? (
              <label className="status">Loading...</label>
            ) : (
              <label className="status">
                {siteId === "" || data?.data?.error ? (
                  <>&nbsp;</>
                ) : (
                  formatCurrency(price)
                )}
              </label>
            )}
          </FormGroup>

          {!readOnly && (
            <div className="mt-3">
              {(() => {
                const hasError = !!getValues("error");
                const statusNotPending = getValues("status") !== "Pending";
                const isMutating = createReservationMutation.isPending;
                const noVehicleInfo = bookingMode === "own" 
                  ? !vehicleId 
                  : !visitorPlate.trim() || !visitorName.trim() || !visitorPhone.trim();
                const isDisabled = hasError || statusNotPending || isMutating || noVehicleInfo;
                
                return (
                  <input
                    disabled={isDisabled}
                    type="submit"
                    className="orangeButton"
                    value={isMutating ? "Creating..." : "Submit"}
                  />
                );
              })()}
            </div>
          )}
        </form>

        {readOnly && cancellable && (
          <Col xs={8}>
            <ButtonWrapper
              buttonText={
                cancelReservationMutation.isPending
                  ? "Cancelling..."
                  : "Cancel Reservation"
              }
              fullWidth={true}
              onClick={() => cancel()}
              disabled={cancelReservationMutation.isPending}
            />
          </Col>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ReservationDetails;
